import { useSyncExternalStore } from "react"

const noSubscription = () => () => undefined

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

// The viewer's zone is only known in the browser. The server render (and
// hydration) uses school time, then class times switch to the viewer's zone,
// so hydration always matches.
export function useViewerTimeZone(): string | null {
  return useSyncExternalStore(noSubscription, browserTimeZone, () => null)
}

function part(parts: Intl.DateTimeFormatPart[], type: string) {
  return parts.find((item) => item.type === type)?.value ?? ""
}

function clock(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).formatToParts(date)
  return {
    time: `${part(parts, "hour")}:${part(parts, "minute")}`,
    period: part(parts, "dayPeriod").toUpperCase(),
  }
}

export function zoneAbbreviation(date: Date, timeZone: string) {
  return part(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(date),
    "timeZoneName",
  )
}

function offset(date: Date, timeZone: string) {
  return part(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longOffset",
    }).formatToParts(date),
    "timeZoneName",
  )
}

// "Sun, Oct 12" in the given zone.
export function dayLabel(date: Date, timeZone: string) {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone,
  }).format(date)
}

// "9:00–11:00 AM PDT", or "11:00 AM–1:00 PM PDT".
export function timeRange(
  startsAt: string,
  endsAt: string | null | undefined,
  timeZone: string,
) {
  const start = new Date(startsAt)
  const from = clock(start, timeZone)
  let range = `${from.time} ${from.period}`
  if (endsAt) {
    const to = clock(new Date(endsAt), timeZone)
    range =
      from.period === to.period
        ? `${from.time}–${to.time} ${to.period}`
        : `${from.time} ${from.period}–${to.time} ${to.period}`
  }
  return `${range} ${zoneAbbreviation(start, timeZone)}`
}

export interface TimedClass {
  date: string
  starts_at?: string | null
  ends_at?: string | null
  formatted_time_range?: string | null
  zone?: string | null
}

export interface ClassTimeText {
  // In the viewer's zone when it differs from the school's.
  primary: string
  // School time, shown only when the viewer's zone differs.
  school: string | null
  // The viewer's day when the class falls on a different date for them.
  localDay: string | null
}

export function classTimeText(
  session: TimedClass,
  schoolTimeZone?: string | null,
  viewerTimeZone?: string | null,
): ClassTimeText {
  if (!session.starts_at || !schoolTimeZone) {
    const fallback = session.formatted_time_range
      ? [session.formatted_time_range, session.zone].filter(Boolean).join(" ")
      : "Time to be confirmed"
    return { primary: fallback, school: null, localDay: null }
  }
  const start = new Date(session.starts_at)
  const school = timeRange(session.starts_at, session.ends_at, schoolTimeZone)
  if (
    !viewerTimeZone ||
    offset(start, viewerTimeZone) === offset(start, schoolTimeZone)
  ) {
    return { primary: school, school: null, localDay: null }
  }
  const viewerDay = dayLabel(start, viewerTimeZone)
  return {
    primary: timeRange(session.starts_at, session.ends_at, viewerTimeZone),
    school,
    localDay: viewerDay === dayLabel(start, schoolTimeZone) ? null : viewerDay,
  }
}
